import React from 'react';
import { Download, User, Heart, ShieldCheck, Lock, Code } from 'lucide-react';
import * as PhosphorIcons from '@phosphor-icons/react';

// Color classes matching the desktop app's ModuleCard
const colorClasses = {
  amber: { text: 'text-amber-400', bg: 'bg-amber-500/10' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  purple: { text: 'text-purple-400', bg: 'bg-purple-500/10' },
  pink: { text: 'text-pink-400', bg: 'bg-pink-500/10' },
  slate: { text: 'text-slate-400', bg: 'bg-slate-500/10' },
  cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  blue: { text: 'text-blue-400', bg: 'bg-blue-500/10' },
};

/**
 * WorkflowCard - Visual card for marketplace listings
 * Portrait (3:4) layout matching the desktop app's game-library style
 */
const WorkflowCard = ({ workflow, onClick, onLike, isLiked }) => {
  // Resolve Phosphor icon component from icon_name string
  const IconComponent = workflow.icon_name
    ? (PhosphorIcons[workflow.icon_name] || PhosphorIcons.FileCode)
    : null;
  const colors = colorClasses[workflow.icon_color] || colorClasses.blue;

  return (
    <div
      onClick={() => onClick(workflow.id)}
      className="group cursor-pointer bg-gray-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all duration-200"
    >
      {/* Thumbnail - 3:4 portrait aspect ratio */}
      <div className="relative aspect-[3/4] bg-gray-800 overflow-hidden">
        {workflow.thumbnail_url ? (
          <>
            <img
              src={workflow.thumbnail_url}
              alt={workflow.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {/* Gradient overlay on image */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </>
        ) : IconComponent ? (
          // Phosphor icon fallback matching desktop app style
          <div className={`w-full h-full flex items-center justify-center ${colors.bg} relative overflow-hidden`}>
            {/* Dot pattern background */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} />
            </div>
            <IconComponent
              size={64}
              weight={workflow.icon_weight || 'regular'}
              className={`${colors.text} relative z-10`}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
        ) : (
          // Generic fallback
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <Code className="h-10 w-10 text-gray-600" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </div>
        )}

        {/* Category badge */}
        <div className="absolute top-2 right-2 flex gap-2 z-10">
          {workflow.visibility === 'private' && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-gray-700/90 text-gray-300 backdrop-blur-sm">
              <Lock className="h-3 w-3" />
              Private
            </span>
          )}
          {workflow.is_isd_ai_creator && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-emerald-600/90 text-white backdrop-blur-sm">
              <ShieldCheck className="h-3.5 w-3.5" />
              Official
            </span>
          )}
          {workflow.price_cents > 0 && (
            <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-600/90 text-white backdrop-blur-sm">
              ${(workflow.price_cents / 100).toFixed(2)}
            </span>
          )}
          <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-600/90 text-white backdrop-blur-sm">
            {workflow.category}
          </span>
        </div>

        {/* Title overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
          <h3 className="text-sm font-semibold text-white drop-shadow-lg line-clamp-2">
            {workflow.title}
          </h3>
        </div>

        {/* Hover overlay with stats */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-4 z-20">
          <div className="flex items-center gap-1 text-white">
            <Download className="h-4 w-4" />
            <span className="text-sm">{workflow.downloads_count?.toLocaleString() || 0}</span>
          </div>
          <div className="flex items-center gap-1 text-white">
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            <span className="text-sm">{workflow.likes_count?.toLocaleString() || 0}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <p className="text-xs text-gray-400 mb-2 line-clamp-2">
          {workflow.description}
        </p>

        {/* Creator info */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
          <div className="flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            <span>{workflow.creator_display_name || 'N/A'}</span>
          </div>
        </div>

        {/* Footer: Like button and date */}
        <div className="flex items-center justify-between">
          {onLike && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLike(workflow.id);
              }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-all ${
                isLiked
                  ? 'text-red-500 hover:bg-red-500/10'
                  : 'text-gray-400 hover:text-red-500 hover:bg-gray-800'
              }`}
              title={isLiked ? 'Unlike' : 'Like'}
            >
              <Heart className={`h-3.5 w-3.5 ${isLiked ? 'fill-red-500' : ''}`} />
              <span className="text-xs font-medium">{workflow.likes_count?.toLocaleString() || 0}</span>
            </button>
          )}
          {workflow.updated_at && (
            <span className="text-xs text-gray-500">
              {new Date(workflow.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowCard;
